import React, { useState, useEffect } from "react";
import {
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import './i18n';

import Main from "./components/main.tsx";
import Login from "./components/login.tsx";
import AllUsers from "./components/allUsers.tsx";
import HospitalList from "./components/hospitalList.tsx";
import HeadquarterList from "./components/headquarterList.tsx";
import LicenseList from "./components/licenseList.tsx";
import TrainingPlanList from "./components/trainingPlanList.tsx";
import Headsets from "./components/headsets.tsx";
import Stream from "./components/stream.tsx";
import Register from "./components/register.tsx";
import ResetPassword from "./components/resetPassword.tsx";
import Verification from "./components/verification.tsx";

import { DefaultRoute } from "./components/DefaultRoute.jsx";
import { ProtectedRoute } from "./components/ProtectedRoute.jsx";
import { AdminRoute } from "./components/AdminRoute.jsx";
import { HeadquarterRoute } from "./components/HeadquarterRoute.jsx";
import AdminMenu from "./components/AdminMenu.tsx";
import HeadquarterMenu from "./components/HeadquarterMenu.tsx";
import HeadsetManagerMenu from "./components/HeadsetManagerMenu.tsx";

import { AuthProvider } from "./firebase/auth.tsx";
import DefaultPageView from "./components/defaultPageView.tsx";
import Footer from "./components/footer.tsx";

// FIXME: CHANGE FIREBASE WRITING RULES

const App: React.FC = () => {
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [headquarters, setHeadquarters] = useState<any[]>([]);
  const [headsets, setHeadsets] = useState<any[]>([]);
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [userList, setUserList] = useState<any[]>([]);
  const [licenses, setLicenses] = useState<any[]>([]); // Lifted state
  const [trainingPlans, setTrainingPlans] = useState<any[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    const oobCode = searchParams.get("oobCode");

    if (oobCode) {
      const newPath =
        mode === "resetPassword"
          ? `/resetPassword?mode=${mode}&oobCode=${oobCode}`
          : mode === "verifyEmail"
          ? `/verification?mode=${mode}&oobCode=${oobCode}`
          : null;

      // Only navigate if the new path is different from the current path
      if (newPath && location.pathname !== newPath) {
        navigate(newPath);
      }
    }
  }, [location.pathname, location.search, navigate]);

  const updateHeadquarters = (newHeadquarter) => {
    setHeadquarters([...headquarters, newHeadquarter]);
  };

  const updateHeadsets = (newHeadset) => {
    setHeadsets([...headsets, newHeadset]);
  };

  const updateHospitals = (newHospital) => {
    setHospitals([...hospitals, newHospital]);
  };

  const updateUserList = (newUser) => {
    if (Array.isArray(newUser)) {
      setUserList([...newUser, ...userList]);
    } else {
      setUserList([newUser, ...userList]);
    }
  };

  const updateLicenseList = (newLicense) => {
    setLicenses([...licenses, newLicense]);
  };

  const updateTrainingPlanList = (newPlan) => {
    if (Array.isArray(newPlan)) {
      setTrainingPlans([...trainingPlans, ...newPlan]);
    } else {
      setTrainingPlans([...trainingPlans, newPlan]);
    }
  };

  return (
    <AuthProvider>
      <div
        style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <DefaultPageView />
        <div style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/register" element={<Register />} />
            <Route path="/resetPassword" element={<ResetPassword />} />
            <Route path="/verification" element={<Verification />} />
            <Route
              path="/login"
              element={
                <DefaultRoute>
                  <Login />
                </DefaultRoute>
              }
            />
            <Route
              path="/main"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <Main />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stream/:streamingUserId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <Stream
                    fullScreenMode={fullScreenMode}
                    setFullScreenMode={setFullScreenMode}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/allUsers"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <AdminRoute>
                    <AdminMenu
                      updateHeadquarters={updateHeadquarters}
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                      selector={"Users"}
                    />
                    <AllUsers userList={userList} setUserList={setUserList} />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/headsets"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <AdminRoute>
                    <AdminMenu
                      updateHeadquarters={updateHeadquarters}
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                      selector={"Headsets"}
                    />
                    <Headsets />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/headquarterList"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <AdminRoute>
                    <AdminMenu
                      updateHeadquarters={updateHeadquarters}
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                      selector={"Headquarters"}
                    />
                    <HeadquarterList
                      headquarters={headquarters}
                      setHeadquarters={setHeadquarters}
                    />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospitalList"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <AdminRoute>
                    <AdminMenu
                      updateHeadquarters={updateHeadquarters}
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                      selector={"Hospitals"}
                    />
                    <HospitalList
                      hospitals={hospitals}
                      setHospitals={setHospitals}
                    />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/licenseList"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <AdminRoute>
                    <AdminMenu
                      updateHeadquarters={updateHeadquarters}
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                      selector={"LicenseList"}
                    />
                    <LicenseList licenses={licenses} setLicenses={setLicenses} />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainingPlanList"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <AdminRoute>
                    <AdminMenu
                      updateHeadquarters={updateHeadquarters}
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                      selector={"TrainingPlanList"}
                    />
                    <TrainingPlanList plans={trainingPlans} setPlans={setTrainingPlans} />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/licenseList/headquarter/:headquarterId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <HeadquarterRoute>
                    <HeadquarterMenu
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                    />
                  </HeadquarterRoute>
                  <LicenseList licenses={licenses} setLicenses={setLicenses} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainingPlanList/headquarter/:headquarterId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <HeadquarterRoute>
                    <HeadquarterMenu
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                    />
                  </HeadquarterRoute>
                  <TrainingPlanList plans={trainingPlans} setPlans={setTrainingPlans} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainingPlanList/headset/:headsetId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <TrainingPlanList plans={trainingPlans} setPlans={setTrainingPlans} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospitalList/headquarter/:headquarterId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <HeadquarterRoute>
                    <HeadquarterMenu
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                    />
                  </HeadquarterRoute>
                  <HospitalList hospitals={hospitals} setHospitals={setHospitals} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/userList/headquarter/:headquarterId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <HeadquarterRoute>
                    <HeadquarterMenu
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                    />
                  </HeadquarterRoute>
                  <AllUsers userList={userList} setUserList={setUserList} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/userList/headset/:headsetId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                    <AllUsers userList={userList} setUserList={setUserList} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-headsets"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                    <HeadsetManagerMenu updateTrainingPlanList={updateTrainingPlanList} />
                    <Headsets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-training-plans"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                    <HeadsetManagerMenu updateTrainingPlanList={updateTrainingPlanList} />
                    <TrainingPlanList plans={trainingPlans} setPlans={setTrainingPlans} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/headsets/headset/:headsetId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                    <Headsets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/headsets/headquarter/:headquarterId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                  <HeadquarterRoute>
                    <HeadquarterMenu
                      updateHeadsets={updateHeadsets}
                      updateHospitals={updateHospitals}
                      updateUserList={updateUserList}
                      updateLicenseList={updateLicenseList}
                      updateTrainingPlanList={updateTrainingPlanList}
                    />
                  </HeadquarterRoute>
                  <Headsets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/userList/:headquarterId/:headsetId"
              element={
                <ProtectedRoute fullScreenMode={fullScreenMode}>
                    <AllUsers userList={userList} setUserList={setUserList} />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
        <Footer />
      </div>
    </AuthProvider>
  );
};

export default App;

